import { getRegion } from '@/helpers/getRegion';
import { getUrlFromConfig, getWebsocketUrlFromConfig } from '@/utils/endpoints';
import { Region } from '@heidi/schema/ml-scribe';

/**
 * Per-region inbox-service origin override. Reads must stay literal
 * `process.env.NEXT_PUBLIC_*` expressions (not dynamic property access) so
 * Next.js's build-time inliner can replace them in the client bundle — see
 * the identical constraint on `readNextPublicEnv` in `src/utils/endpoints.ts`.
 */
const readInboxServiceUrlEnvForRegion = (region: Region): string | undefined => {
  switch (region) {
    case Region.AU:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_AU;
    case Region.UK:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_UK;
    case Region.US:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_US;
    case Region.CA:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_CA;
    case Region.EU:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_EU;
    case Region.CH:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_CH;
    case Region.JP_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_JP_AZ1;
    case Region.AU_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_AU_AZ1;
    case Region.UAE_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_UAE_AZ1;
    case Region.US_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_US_AZ1;
    case Region.QAT_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_QAT_AZ1;
    case Region.SE_AZ1:
      return process.env.NEXT_PUBLIC_INBOX_SERVICE_URL_SE_AZ1;
    default:
      return undefined;
  }
};

/**
 * Get domains for the current region.
 * @param region - optional region override, used for SSR (e.g. EHR Launch).
 *   When omitted, delegates to {@link getRegion} for the unified fallback chain.
 */
export const getDomains = (region?: Region) => {
  const finalRegion = region ?? getRegion();

  const baseUrl = getUrlFromConfig(finalRegion);
  const baseWebsocketUrl = getWebsocketUrlFromConfig(finalRegion);

  // Standalone inbox-service origin (path `/api/v1/` — distinct from
  // ml-scribe's `/api/v1/ml-scribe/`). The inbox-service runs on its own host,
  // so an origin is REQUIRED to use the inbox feature.
  //
  // There is intentionally NO fallback to the Scribe gateway origin: silently
  // pointing inbox calls at the ml-scribe gateway would hit the wrong service.
  // When no origin resolves, `inboxServiceUrl` is `undefined` and the inbox
  // feature stays gated off (the `useIsDocumentInboxEnabled` umbrella gate
  // enforces this and logs the misconfiguration).
  //
  // Data residency: unlike ml-scribe (`scribeDomains`, hardcoded per-region
  // hosts), inbox-service is a newer, currently single-origin service, so a
  // per-region override is opt-in rather than a hardcoded map. This routes
  // full PHI document content, so once inbox-service deploys region-sharded
  // instances, set the matching `NEXT_PUBLIC_INBOX_SERVICE_URL_<REGION>` var
  // per environment — it takes precedence over the shared fallback below.
  // Until then every region resolves to the single configured origin.
  const inboxServiceOrigin = (
    readInboxServiceUrlEnvForRegion(finalRegion) ??
    process.env.NEXT_PUBLIC_INBOX_SERVICE_URL
  )?.replace(/\/+$/, '');

  return {
    baseV1Url: baseUrl + '/api/v1/ml-scribe/',
    baseV1IdentityBrokerUrl: baseUrl + '/api/v1/identity-broker/',
    baseV2Url: baseUrl + '/api/v2/ml-scribe/',
    openApiBaseUrl: baseUrl + '/api/v2/ml-scribe/open-api/',
    baseV2_1Url: baseUrl + '/api/v2-1/ml-scribe/',
    baseV2_2Url: baseUrl + '/api/v2-2/ml-scribe/',
    inboxServiceUrl: inboxServiceOrigin ? `${inboxServiceOrigin}/api/v1/` : undefined,
    // Bare origin (no `/api/v1/` suffix) for `api/upload-client.ts`, which
    // builds its own `${baseUrl}/api/v1/...` paths internally.
    inboxServiceOrigin,
    websocketV1Url: baseWebsocketUrl,
  };
};

export const getDomain = (endPointVersion: 'v1' | 'v2' | 'v2-1' | 'v2-2') => {
  const region = getRegion();
  const baseUrl = getUrlFromConfig(region);
  return baseUrl + `/api/${endPointVersion}/ml-scribe/`;
};
